import { setSession, clearSession } from './session';

const TOKEN_KEY = 'auth_token';

/**
 * Summary: 取得 sessionStorage 中的 JWT Token。
 * Params: 無
 * Return: string | null
 * Remarks: storage key 為 auth_token
 */
export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

/**
 * Summary: 寫入 JWT Token 至 sessionStorage。
 * Params: token {string} JWT 字串
 * Return: void
 * Remarks: 通常由 login() 內部呼叫
 */
export function setToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

/**
 * Summary: 清除 sessionStorage 中的 JWT Token。
 * Params: 無
 * Return: void
 * Remarks: logout() 與 checkAuth 失敗時呼叫
 */
export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Summary: 帶 Bearer Token 的 fetch 封裝。
 * Params:
 *   - url {string} API 路徑
 *   - options {RequestInit} 選填
 * Return: Promise<Response>
 * Remarks: 自動附加 Authorization 與 Content-Type
 */
export function authFetch(url, options = {}) {
  const token = getToken();

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
}

/**
 * Summary: 呼叫 GET /api/auth/check 驗證登入，成功時同步 session。
 * Params: 無
 * Return: Promise<{ authenticated: boolean, session?: Session }>
 * Remarks: ProtectedRoute 使用；401 時自動清除登入狀態
 */
export async function checkAuth() {
  const token = getToken();

  if (!token) {
    return { authenticated: false };
  }

  const res = await authFetch('/api/auth/check');

  if (!res.ok) {
    clearToken();
    clearSession();
    return { authenticated: false };
  }

  const data = await res.json();

  if (data.session) {
    setSession(data.session);
  }

  return data;
}

/**
 * Summary: 呼叫 POST /api/auth/login，成功後寫入 token 與 session。
 * Params:
 *   - userId {string} 登入帳號
 *   - password {string} 明文密碼
 * Return: Promise<{ token: string, session: Session }>
 * Remarks: 失敗 throw Error；Login.jsx 使用
 */
export async function login(userId, password) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, password })
  });

  const contentType = res.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    throw new Error('登入服務暫時無法使用，請稍後再試');
  }

  const data = await res.json();

  if (!res.ok) {
    const error = new Error(data.error || '登入失敗');
    error.status = res.status;
    throw error;
  }

  setToken(data.token);

  if (data.session) {
    setSession(data.session);
  }

  return data;
}

/**
 * Summary: 清除 token 與 session（登出）。
 * Params: 無
 * Return: Promise<void>
 * Remarks: MainLayout 登出按鈕使用
 */
export async function logout() {
  clearToken();
  clearSession();
}

export { getSession, getUserId, getRole, getPermission, hasPermission, getProfile, getDisplayName } from './session';
